import React from "react";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import { Typography, Button, Tooltip } from "@material-ui/core";
import { withNamespaces } from "react-i18next";

import { ReactComponent as UnlockSvg } from "../../../assets/unlocked.svg";
import { toFixed } from "../../../constants/constants";
import bigInt from "big-integer";
import moment from "moment";

const styles = (theme) => ({
  poolManageCard: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: theme?.colors?.presale?.cardBackground,
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "30px",
    marginBottom: "30px",
    padding: "20px",
    [theme.breakpoints.up("sm")]: {
      borderRadius: "30px",
      padding: "32px",
    },
  },
  unlockSvg: {
    "& .a": {
      fill: "var(--svg-fill-color, " + theme?.colors?.presale?.cardText + ")",
    },
  },
  poolTitle: {
    color: theme?.colors?.presale?.cardTitle,
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "8px",
    [theme.breakpoints.up("sm")]: {
      marginBottom: "16px",
    },
  },
  yourInformationContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "space-between",
    },
  },
  yourTokenInfo: {
    display: "grid",
    width: "100%",
    gridGap: "10px 15px",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gridGap: "15px 20px",
    },
  },
  lockLiqContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  },
  yourTokenInfoItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginRight: "0px",
    justifyContent: "end",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      marginBottom: "0px",
    },
  },
  infoHeading: {
    color: theme?.colors?.presale?.cardText,
    fontWeight: "normal",
    lineHeight: "normal",
    marginBottom: "0px",

    fontSize: "14px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "16px",
    },
  },
  infoValue: {
    color: theme?.colors?.presale?.addressValueText,
    fontSize: "15px",
    fontWeight: "normal",
    lineHeight: "20px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "20px",
      lineHeight: "25px",
    },
  },
  actionButton: {
    "&:disabled": {
      opacity: "0.7",
      color: theme?.colors?.wallet?.buttonTextSecondary,
      cursor: "not-allowed",
    },
    borderRadius: "20px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: theme?.colors?.wallet?.buttonBackground,
    color: theme?.colors?.wallet?.buttonTextSecondary,
    textTransform: "capitalize",
    "&:hover": {
      backgroundColor: theme?.colors?.wallet?.buttonBackgroundHover,
    },
    fontFamily: "Poppins",

    width: "100%",
    marginTop: "12px",

    padding: "12px",
    [theme.breakpoints.up("md")]: {
      marginTop: "15px",
      padding: "16px",
    },
  },
  buttonLabel: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "18px",
  },
  tokenTooltip: {
    fontWeight: "normal",
    fontSize: "15px",
    lineHeight: "18px",
  },
});

const ManageCard = ({
  classes,
  poolInfo,
  onCollect,
  onCancelPresale,
  // onSendUnsoldTokens,
  onAllowClaim,
  onLockLiq,
}) => {
  const totalCollected = parseFloat(
    toFixed(
      bigInt(poolInfo.totalCollectedWei),
      poolInfo?.fundingTokenDecimals || 18,
      6
    )
  );

  const isExpired = moment
    .unix(poolInfo?.cakeLiquidityAddingTime)
    .isBefore(moment.now());

  const hardCap = parseFloat(
    toFixed(
      bigInt(poolInfo.hardCapInWei),
      poolInfo?.fundingTokenDecimals || 18,
      6
    )
  );
  const softCap = parseFloat(
    toFixed(
      bigInt(poolInfo.softCapInWei),
      poolInfo?.fundingTokenDecimals || 18,
      6
    )
  );

  const tokenActionPart = (
    heading,
    value,
    buttonText,
    clickHandler,
    disabled = false,
    tooltip = false,
    tooltipText = null
  ) => {
    return (
      <div className={classes.yourTokenInfoItem}>
        <Typography variant="h5" className={classes.infoHeading}>
          {heading}
        </Typography>
        <Typography variant="h2" className={classes.infoValue}>
          {value}
        </Typography>
        {tooltip && disabled && (
          <Tooltip title={tooltipText}>
            <span style={{ width: "100%" }}>
              <Button
                classes={{
                  root: classes.actionButton,
                  label: classes.buttonLabel,
                }}
                onClick={() => {
                  if (clickHandler) {
                    clickHandler();
                  }
                }}
                disabled={disabled}
              >
                {buttonText}
              </Button>
            </span>
          </Tooltip>
        )}
        {tooltip && !disabled && (
          <Tooltip title={tooltipText}>
            <Button
              classes={{
                root: classes.actionButton,
                label: classes.buttonLabel,
              }}
              onClick={() => {
                if (clickHandler) {
                  clickHandler();
                }
              }}
              disabled={disabled}
            >
              {buttonText}
            </Button>
          </Tooltip>
        )}
        {!tooltip && (
          <Button
            classes={{
              root: classes.actionButton,
              label: classes.buttonLabel,
            }}
            onClick={() => {
              if (clickHandler) {
                clickHandler();
              }
            }}
            disabled={disabled}
          >
            {buttonText}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Typography variant="h3" className={classes.poolTitle}>
        Pool Management
      </Typography>
      <div className={classes.poolManageCard}>
        <div className={classes.yourInformationContainer}>
          <div className={classes.yourTokenInfo}>
            {tokenActionPart(
              "Collect Fund Raised",
              "",
              "Collect",
              onCollect,
              !(
                poolInfo.presaleCancelled ||
                (poolInfo.presaleType === "0" && poolInfo.claimAllowed) ||
                (poolInfo.presaleType !== "0" && poolInfo.cakeLiquidityAdded)
              ),
              true,
              "Collect all funds raised in your presale."
            )}
            {tokenActionPart(
              "Cancel Presale",
              "",
              "Cancel",
              onCancelPresale,
              poolInfo.cakeLiquidityAdded ||
                poolInfo.claimAllowed ||
                poolInfo.presaleCancelled,
              true,
              "Cancel the current presale and transfer tokens back to you"
            )}
            {/* {tokenActionPart(
            "Send Unsold Tokens",
            "",
            "Claim Unsold",
            onSendUnsoldTokens,
            false,
            true,
            "Send Unsold tokens back to presale creator"
            )} */}
            {poolInfo.presaleType === "0" &&
              tokenActionPart(
                !poolInfo.claimAllowed ? "Enable Claiming" : "Enabled",
                "",
                "Enable Claim",
                onAllowClaim,
                poolInfo.claimAllowed ||
                  poolInfo.cakeLiquidityAdded ||
                  totalCollected < softCap,
                true,
                "Allow users to claim their tokens."
              )}
            {poolInfo.presaleType !== "0" && (
              <div className={classes.lockLiqContainer}>
                <UnlockSvg
                  className={classes.unlockSvg}
                  style={{ width: "35px", height: "35px", fill: "#000000" }}
                />

                <Button
                  classes={{
                    root: classes.actionButton,
                    label: classes.buttonLabel,
                  }}
                  onClick={() => {
                    if (onLockLiq) {
                      onLockLiq();
                    }
                  }}
                  disabled={
                    !isExpired &&
                    totalCollected >= softCap &&
                    totalCollected < hardCap
                  }
                >
                  Lock Liq and List
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default withNamespaces()(withRouter(withStyles(styles)(ManageCard)));
